import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, Plus, Search, Upload } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { useSectionAccess } from '../../hooks/useSectionAccess';
import { funnelStagePresentation } from '../placeholders/funnelStageUtils';

const formatMoneyCompact = (value) => {
  const v = Number(value || 0);
  if (!Number.isFinite(v)) return '$0';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(v / 1_000)}k`;
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v);
};

const Stacked = ({ top, bottom, topBold = true, topColor, bottomColor, mutedColor }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topBold ? 700 : 500, fontSize: '12px', color: topColor || '#0f172a' }}>{top || '—'}</div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: bottomColor || mutedColor || '#64748b', marginTop: '1px' }}>{bottom}</div>
    ) : null}
  </div>
);

/** Pill colors for organization lead status (A001_organizations.status) */
const leadStatusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '')
    .trim()
    .toLowerCase();
  const base = { label: statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—' };
  if (s === 'recurrente') {
    return {
      ...base,
      bg: isDarkMode ? '#134e4a' : '#ccfbf1',
      color: isDarkMode ? '#5eead4' : '#0f766e',
      border: isDarkMode ? '#14b8a6' : '#99f6e4',
    };
  }
  if (s === 'cliente') {
    return {
      ...base,
      bg: isDarkMode ? '#1e3a5f' : '#dbeafe',
      color: isDarkMode ? '#93c5fd' : '#1d4ed8',
      border: isDarkMode ? '#3b82f6' : '#93c5fd',
    };
  }
  if (s === 'calificado') {
    return {
      ...base,
      bg: isDarkMode ? '#4c1d95' : '#ede9fe',
      color: isDarkMode ? '#c4b5fd' : '#6d28d9',
      border: isDarkMode ? '#8b5cf6' : '#c4b5fd',
    };
  }
  if (s === 'lead' || s === 'prospecto') {
    return {
      ...base,
      bg: isDarkMode ? '#7c2d12' : '#ffedd5',
      color: isDarkMode ? '#fdba74' : '#9a3412',
      border: isDarkMode ? '#ea580c' : '#fdba74',
    };
  }
  if (s === 'activo') {
    return {
      ...base,
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
    };
  }
  if (s === 'inactivo' || s === 'cerrado' || s === 'baja') {
    return {
      ...base,
      bg: isDarkMode ? '#3f3f46' : '#f4f4f5',
      color: isDarkMode ? '#d4d4d8' : '#52525b',
      border: isDarkMode ? '#52525b' : '#d4d4d8',
    };
  }
  if (s === 'pendiente' || s === 'en evaluación' || s === 'en evaluacion') {
    return {
      ...base,
      bg: isDarkMode ? '#1e3a5f' : '#e0f2fe',
      color: isDarkMode ? '#7dd3fc' : '#0369a1',
      border: isDarkMode ? '#0284c7' : '#7dd3fc',
    };
  }
  return {
    ...base,
    bg: isDarkMode ? '#27272a' : '#f0ebe3',
    color: isDarkMode ? '#e4e4e7' : '#44403c',
    border: isDarkMode ? '#3f3f46' : '#d6d0c4',
  };
};

const EMPTY = '__EMPTY__';

const getRowStatusValue = (row, statusField = 'status') =>
  String(row?.[statusField] ?? row?.status ?? '').trim();

const getSortValue = (row, sortKey, statusField = 'status') => {
  switch (sortKey) {
    case 'org_name':
      return String(row.org_name || '').toLowerCase();
    case 'units': {
      const u = Array.isArray(row.primary_unit_names) ? row.primary_unit_names : [];
      return String(u[0] || '').toLowerCase();
    }
    case 'contact':
      return (row.contactNames || []).join(' ').toLowerCase();
    case 'location':
      return `${row.country || ''} ${row.region || ''} ${row.commune || ''}`.toLowerCase();
    case 'segment':
      return String(row.segment || '').toLowerCase();
    case 'assignee':
      return String(row.assignee_name || '').toLowerCase();
    case 'quotes':
      return Number(row.quotations_count) || 0;
    case 'payment':
      return String(row.payment_terms || '').toLowerCase();
    case 'value':
      return Number(row.sales_amount) || 0;
    case 'status':
      return getRowStatusValue(row, statusField).toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir, statusField = 'status') => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey, statusField);
  const vb = getSortValue(b, sortKey, statusField);
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const TABLE_COLUMNS = [
  { key: 'Nombre', align: 'left', sortKey: 'org_name' },
  { key: 'Subunidades', align: 'left', sortKey: 'units' },
  { key: 'Contacto', align: 'left', sortKey: 'contact' },
  { key: 'Ubicación', align: 'left', sortKey: 'location' },
  { key: 'Segmento', align: 'left', sortKey: 'segment' },
  { key: 'Responsable', align: 'left', sortKey: 'assignee' },
  { key: 'Cotiz. / Ventas', align: 'center', sortKey: 'quotes' },
  { key: 'Pago', align: 'left', sortKey: 'payment' },
  { key: 'Valor', align: 'left', sortKey: 'value' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

/** Valores para acciones masivas (segmento / estado en A001_organizations) */
const BULK_SEGMENT_VALUES = ['B2B', 'B2C', 'B2G', 'B2B2C', 'Sin Asignar'];
const BULK_STATUS_VALUES = [
  'Lead',
  'Calificado',
  'Cliente',
  'Recurrente',
  'Inactivo',
  'Cerrado',
  'Baja',
];
/** Valor de opción «vaciar» en desplegables de texto (canal, industria, etc.) */
const BULK_CLEAR = '__CLEAR__';

/**
 * Tabla de organizaciones (dashboard): barra lateral según estado, avatar, columnas apiladas.
 */
const Table3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Organizaciones',
  addButtonLabel = 'Nueva organización',
  subtitleOverride,
  onRowNameClick,
  onAdd,
  /** Callback para abrir carga masiva (CSV) */
  onBulkImport,
  /** Descarga plantilla Excel junto al botón de carga masiva */
  onBulkImportTemplate,
  bulkImportLabel = 'Carga masiva',
  hideChrome = false,
  toolbarExtra = null,
  /** ({ organizationIds, ...campos }) → persistir vía API; si no se pasa, no se muestra selección masiva */
  onBulkUpdate,
  /** (organizationIds[]) → eliminar en bloque (soft delete); si no se pasa, no hay botón eliminar */
  onBulkDelete,
  /** Opciones { value: userId, label } para responsable (assigned_user_id) */
  bulkExecutiveOptions = [],
  /** Campo mostrado/filtrado en columna Estado: `status` (ciclo lead) o `funnel_stage` */
  statusField = 'status',
}) => {
  const ui = useUIColors();
  const access = useSectionAccess();
  const addHandler = access.canCreate ? onAdd : undefined;
  const bulkImportHandler = access.canCreate ? onBulkImport : undefined;
  const bulkDeleteHandler = access.canDelete ? onBulkDelete : undefined;
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  /** ~35% menos alto: paddings y bloques principales escalados a ~65% */
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSegment, setFilterSegment] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [sortColumn, setSortColumn] = useState('org_name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkSeg, setBulkSeg] = useState('');
  const [bulkStat, setBulkStat] = useState('');
  const [bulkChannel, setBulkChannel] = useState('');
  const [bulkIndustry, setBulkIndustry] = useState('');
  const [bulkSubindustry, setBulkSubindustry] = useState('');
  const [bulkExec, setBulkExec] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkUpdate || bulkDeleteHandler);
  const colCount = showBulkChrome ? TABLE_COLUMNS.length + 1 : TABLE_COLUMNS.length;

  const statusUsesFunnel = statusField === 'funnel_stage';

  const { statusOptions, segmentOptions, regionOptions, countryOptions } = useMemo(() => {
    const statusMap = new Map();
    const segmentMap = new Map();
    const regionMap = new Map();
    const countryMap = new Map();
    rows.forEach((r) => {
      const st = getRowStatusValue(r, statusField);
      const sk = st || EMPTY;
      const sl = st || (statusUsesFunnel ? 'Sin etapa' : 'Sin estado');
      statusMap.set(sk, sl);
      const seg = String(r.segment || '').trim();
      const sgk = seg || EMPTY;
      const sgl = seg || 'Sin Asignar';
      segmentMap.set(sgk, sgl);
      const reg = String(r.region || '').trim();
      const rgk = reg || EMPTY;
      const rgl = reg || 'Sin región';
      regionMap.set(rgk, rgl);
      const country = String(r.country || '').trim();
      const ck = country || EMPTY;
      const cl = country || 'Sin país';
      countryMap.set(ck, cl);
    });
    const sortEntries = (entries) =>
      [...entries].sort((a, b) => a[1].localeCompare(b[1], 'es', { sensitivity: 'base' }));
    return {
      statusOptions: sortEntries(statusMap.entries()),
      segmentOptions: sortEntries(segmentMap.entries()),
      regionOptions: sortEntries(regionMap.entries()),
      countryOptions: sortEntries(countryMap.entries()),
    };
  }, [rows, statusField, statusUsesFunnel]);

  const { channelBulkOptions, industryBulkOptions, subindustryBulkOptions } = useMemo(() => {
    const ch = new Set();
    const ind = new Set();
    const sub = new Set();
    rows.forEach((r) => {
      const c = String(r.channel || '').trim();
      if (c) ch.add(c);
      const i = String(r.industry || '').trim();
      if (i) ind.add(i);
      const s = String(r.subindustry || '').trim();
      if (s) sub.add(s);
    });
    const sortArr = (set) => [...set].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    return {
      channelBulkOptions: sortArr(ch),
      industryBulkOptions: sortArr(ind),
      subindustryBulkOptions: sortArr(sub),
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (filterStatus) {
        const key = getRowStatusValue(row, statusField) || EMPTY;
        if (key !== filterStatus) return false;
      }
      if (filterSegment) {
        const key = String(row.segment || '').trim() || EMPTY;
        if (key !== filterSegment) return false;
      }
      if (filterRegion) {
        const key = String(row.region || '').trim() || EMPTY;
        if (key !== filterRegion) return false;
      }
      if (filterCountry) {
        const key = String(row.country || '').trim() || EMPTY;
        if (key !== filterCountry) return false;
      }
      if (!q) return true;
      const units = Array.isArray(row.primary_unit_names) ? row.primary_unit_names : [];
      const hay = [
        row.org_name,
        row.rut,
        ...(row.contactNames || []),
        row.country,
        row.region,
        row.commune,
        row.segment,
        row.status,
        row.funnel_stage,
        row.channel,
        row.industry,
        row.payment_terms,
        row.last_executive_name,
        row.created_by,
        row.assignee_name,
        ...units,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, searchQuery, filterStatus, filterSegment, filterRegion, filterCountry, statusField]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir, statusField));
    return arr;
  }, [filteredRows, sortColumn, sortDir, statusField]);

  const visibleIds = useMemo(() => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id)), [sortedRows]);

  const allVisibleSelected =
    showBulkChrome && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected =
    showBulkChrome && visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el || el.type !== 'checkbox') return;
    el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected, showBulkChrome]);

  const toggleRowSelected = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const clearSelection = () => setSelectedIds([]);

  useEffect(() => {
    if (selectedIds.length === 0) {
      setBulkSeg('');
      setBulkStat('');
      setBulkChannel('');
      setBulkIndustry('');
      setBulkSubindustry('');
      setBulkExec('');
    }
  }, [selectedIds.length]);

  const applyBulk = async () => {
    if (!onBulkUpdate || selectedIds.length === 0) return;
    const patch = {};
    if (bulkSeg) patch.segment = bulkSeg;
    if (bulkStat) patch.status = bulkStat;
    if (bulkChannel === BULK_CLEAR) patch.channel = '';
    else if (bulkChannel) patch.channel = bulkChannel;
    if (bulkIndustry === BULK_CLEAR) patch.industry = '';
    else if (bulkIndustry) patch.industry = bulkIndustry;
    if (bulkSubindustry === BULK_CLEAR) patch.subindustry = '';
    else if (bulkSubindustry) patch.subindustry = bulkSubindustry;
    if (bulkExec === BULK_CLEAR) patch.assigned_user_id = 0;
    else if (bulkExec) patch.assigned_user_id = Number(bulkExec);
    if (Object.keys(patch).length === 0) {
      setBulkError('Elija al menos un campo distinto de «Sin cambio».');
      return;
    }
    setBulkPending(true);
    setBulkError('');
    try {
      await onBulkUpdate({ organizationIds: selectedIds, ...patch });
      setSelectedIds([]);
    } catch (e) {
      setBulkError(e?.message || 'No se pudo aplicar el cambio.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleteHandler || selectedIds.length === 0) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Eliminar ${n} organización${n === 1 ? '' : 'es'} seleccionada${n === 1 ? '' : 's'}? Se marcarán como inactivas en el sistema.`
    );
    if (!ok) return;
    setBulkPending(true);
    setBulkError('');
    try {
      await bulkDeleteHandler(selectedIds);
      setSelectedIds([]);
    } catch (e) {
      setBulkError(e?.message || 'No se pudo eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(filterStatus) ||
    Boolean(filterSegment) ||
    Boolean(filterRegion) ||
    Boolean(filterCountry);

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterSegment('');
    setFilterRegion('');
    setFilterCountry('');
  };

  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} registros · A001_organizations`
        : `${rows.length} registros · A001_organizations`);
  const subtitleFinal = subtitleOverride || subtitle;

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const bulkSelectStyle = {
    height: '32px',
    padding: '0 8px',
    borderRadius: '6px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '12px',
    minWidth: '120px',
    maxWidth: '220px',
    cursor: 'pointer',
    outline: 'none',
  };

  const bulkLabelStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '11px',
    color: muted,
    fontWeight: 600,
  };

  const executiveBulkChoices = useMemo(
    () => (Array.isArray(bulkExecutiveOptions) ? bulkExecutiveOptions : []).filter((o) => o && String(o.value).length > 0),
    [bulkExecutiveOptions]
  );

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0, flex: '1 1 auto' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitleFinal}</p>
        </div>
        {(addHandler || bulkImportHandler) ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {addHandler ? (
              <button
                type="button"
                onClick={addHandler}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: '36px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#16a34a',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <Plus size={16} /> {addButtonLabel}
              </button>
            ) : null}
            {bulkImportHandler ? (
              <div style={{ display: 'inline-flex', alignItems: 'stretch' }}>
                <button
                  type="button"
                  onClick={bulkImportHandler}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    height: '36px',
                    padding: '0 12px',
                    borderRadius: onBulkImportTemplate ? '8px 0 0 8px' : '8px',
                    border: `1px solid ${inputBorder}`,
                    borderRight: onBulkImportTemplate ? 'none' : `1px solid ${inputBorder}`,
                    background: isDarkMode ? '#27272a' : '#fff',
                    color: text,
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  <Upload size={16} /> {bulkImportLabel}
                </button>
                {onBulkImportTemplate ? (
                  <button
                    type="button"
                    onClick={onBulkImportTemplate}
                    title="Descargar plantilla Excel"
                    aria-label="Descargar plantilla Excel"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '34px',
                      height: '36px',
                      padding: 0,
                      borderRadius: '0 8px 8px 0',
                      border: `1px solid ${inputBorder}`,
                      background: isDarkMode ? '#3f3f46' : '#f8fafc',
                      color: text,
                      cursor: 'pointer',
                    }}
                  >
                    <ChevronDown size={16} />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-start',
        }}
      >
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            fontSize: '11px',
            color: muted,
            fontWeight: 600,
            flex: '1 1 240px',
            minWidth: '200px',
          }}
        >
          Buscar
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              aria-hidden
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: muted,
                pointerEvents: 'none',
              }}
            />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nombre, RUT, contacto, ubicación…"
              aria-label="Buscar en la tabla de organizaciones"
              style={{
                width: '100%',
                height: '36px',
                padding: '0 12px 0 40px',
                borderRadius: '8px',
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: muted, fontWeight: 600 }}>
          Estado
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={selectStyle}
            aria-label="Filtrar por estado"
          >
            <option value="">Todos</option>
            {statusOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: muted, fontWeight: 600 }}>
          Segmento
          <select
            value={filterSegment}
            onChange={(e) => setFilterSegment(e.target.value)}
            style={selectStyle}
            aria-label="Filtrar por segmento"
          >
            <option value="">Todas</option>
            {segmentOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: muted, fontWeight: 600 }}>
          País
          <select
            value={filterCountry}
            onChange={(e) => setFilterCountry(e.target.value)}
            style={selectStyle}
            aria-label="Filtrar por país"
          >
            <option value="">Todos</option>
            {countryOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: muted, fontWeight: 600 }}>
          Región
          <select
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            style={selectStyle}
            aria-label="Filtrar por región"
          >
            <option value="">Todas</option>
            {regionOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {toolbarExtra}
        {hasActiveFilters ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', lineHeight: '15px', display: 'block' }} aria-hidden>
              {'\u00a0'}
            </span>
            <button
              type="button"
              onClick={clearFilters}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                border: `1px solid ${inputBorder}`,
                background: isDarkMode ? '#27272a' : '#f0ebe3',
                color: text,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Limpiar filtros
            </button>
          </div>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#f5f0e8',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '13px', color: text }}>
            {selectedIds.length} seleccionada{selectedIds.length === 1 ? '' : 's'}
            {onBulkUpdate ? ' — asignar valores' : ''}
          </div>
          {onBulkUpdate ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px 12px',
              alignItems: 'flex-end',
            }}
          >
            <label style={bulkLabelStyle}>
              Segmento
              <select
                value={bulkSeg}
                onChange={(e) => setBulkSeg(e.target.value)}
                disabled={bulkPending}
                style={bulkSelectStyle}
                aria-label="Segmento masivo"
              >
                <option value="">Sin cambio</option>
                {BULK_SEGMENT_VALUES.map((seg) => (
                  <option key={seg} value={seg}>
                    {seg}
                  </option>
                ))}
              </select>
            </label>
            <label style={bulkLabelStyle}>
              Estado
              <select
                value={bulkStat}
                onChange={(e) => setBulkStat(e.target.value)}
                disabled={bulkPending}
                style={bulkSelectStyle}
                aria-label="Estado masivo"
              >
                <option value="">Sin cambio</option>
                {BULK_STATUS_VALUES.map((stLabel) => (
                  <option key={stLabel} value={stLabel}>
                    {stLabel}
                  </option>
                ))}
              </select>
            </label>
            <label style={bulkLabelStyle}>
              Canal
              <select
                value={bulkChannel}
                onChange={(e) => setBulkChannel(e.target.value)}
                disabled={bulkPending}
                style={bulkSelectStyle}
                aria-label="Canal masivo"
              >
                <option value="">Sin cambio</option>
                <option value={BULK_CLEAR}>Dejar vacío</option>
                {channelBulkOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label style={bulkLabelStyle}>
              Industria
              <select
                value={bulkIndustry}
                onChange={(e) => setBulkIndustry(e.target.value)}
                disabled={bulkPending}
                style={bulkSelectStyle}
                aria-label="Industria masiva"
              >
                <option value="">Sin cambio</option>
                <option value={BULK_CLEAR}>Dejar vacío</option>
                {industryBulkOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label style={bulkLabelStyle}>
              Subindustria
              <select
                value={bulkSubindustry}
                onChange={(e) => setBulkSubindustry(e.target.value)}
                disabled={bulkPending}
                style={bulkSelectStyle}
                aria-label="Subindustria masiva"
              >
                <option value="">Sin cambio</option>
                <option value={BULK_CLEAR}>Dejar vacío</option>
                {subindustryBulkOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label style={bulkLabelStyle}>
              Responsable
              <select
                value={bulkExec}
                onChange={(e) => setBulkExec(e.target.value)}
                disabled={bulkPending}
                style={bulkSelectStyle}
                aria-label="Responsable masivo"
              >
                <option value="">Sin cambio</option>
                <option value={BULK_CLEAR}>Dejar vacío</option>
                {executiveBulkChoices.map((o) => (
                  <option key={String(o.value)} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={bulkPending}
              onClick={applyBulk}
              style={{
                height: '32px',
                padding: '0 14px',
                borderRadius: '6px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
              }}
            >
              Aplicar
            </button>
          </div>
          ) : null}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            {bulkDeleteHandler ? (
              <button
                type="button"
                disabled={bulkPending}
                onClick={handleBulkDelete}
                style={{
                  height: '32px',
                  padding: '0 14px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: bulkPending ? 'not-allowed' : 'pointer',
                  opacity: bulkPending ? 0.7 : 1,
                }}
              >
                Eliminar seleccionadas
              </button>
            ) : null}
            <button
              type="button"
              disabled={bulkPending}
              onClick={clearSelection}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: muted,
                fontSize: '12px',
                fontWeight: 600,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
              }}
            >
              Quitar selección
            </button>
          </div>
          {bulkError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>{bulkError}</p>
          ) : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: showBulkChrome ? '1160px' : '1120px' }}>
          <thead>
            <tr style={{ background: headerBg }}>
              {showBulkChrome ? (
                <th
                  scope="col"
                  style={{
                    width: '40px',
                    padding: `${thPadV}px 6px`,
                    borderBottom: `1px solid ${border}`,
                    verticalAlign: 'middle',
                  }}
                >
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    disabled={loading || sortedRows.length === 0 || bulkPending}
                    aria-label="Seleccionar todas las filas visibles"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map(({ key: h, align, sortKey: sk }) => (
                <th
                  key={h}
                  scope="col"
                  onClick={() => sk && handleSortClick(sk)}
                  onKeyDown={(e) => {
                    if (!sk) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSortClick(sk);
                    }
                  }}
                  aria-sort={
                    sk && sortColumn === sk ? (sortDir === 'asc' ? 'ascending' : 'descending') : sk ? 'none' : undefined
                  }
                  tabIndex={sk ? 0 : undefined}
                  style={{
                    textAlign: align,
                    padding: `${thPadV}px ${padH + 4}px`,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    borderBottom: `1px solid ${border}`,
                    cursor: sk ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: align === 'center' ? 'center' : 'flex-start',
                      gap: '6px',
                      width: align === 'center' ? '100%' : 'auto',
                    }}
                  >
                    {h}
                    {sk ? (
                      sortColumn === sk ? (
                        sortDir === 'asc' ? (
                          <ArrowUp size={13} strokeWidth={2.5} aria-hidden />
                        ) : (
                          <ArrowDown size={13} strokeWidth={2.5} aria-hidden />
                        )
                      ) : (
                        <ArrowUpDown size={13} strokeWidth={2} style={{ opacity: 0.4 }} aria-hidden />
                      )
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Cargando organizaciones…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  {error || 'No hay organizaciones para mostrar.'}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Ningún resultado con la búsqueda o filtros actuales.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const initial = String(row.org_name || '?').trim().charAt(0).toUpperCase();
                const c1 = row.contactNames?.[0] || '';
                const c2 = row.contactNames?.[1] || '';
                const channelIndustry = [row.channel, row.industry].filter(Boolean).join(' · ') || '—';
                const units = Array.isArray(row.primary_unit_names) ? row.primary_unit_names : [];
                const u1 = units[0] || '';
                const u2 = units[1] || '';
                const assigneeName = String(row.assignee_name || '')
                  .trim()
                  .replace(/^system$/i, '');
                const st = statusUsesFunnel
                  ? funnelStagePresentation(getRowStatusValue(row, statusField), isDarkMode)
                  : leadStatusPresentation(row.status, isDarkMode);
                const statusSecondary = statusUsesFunnel
                  ? String(row.status || '').trim() || '—'
                  : row.last_executive_name || '—';
                const statusSecondaryActive = statusUsesFunnel
                  ? Boolean(String(row.status || '').trim())
                  : Boolean(row.last_executive_name);
                const rowId = Number(row.id);
                const rowSelected = showBulkChrome && selectedIds.includes(rowId);

                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{
                          padding: `${padV}px 6px`,
                          verticalAlign: 'middle',
                          width: '40px',
                          textAlign: 'center',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={rowSelected}
                          onChange={() => toggleRowSelected(row.id)}
                          disabled={bulkPending}
                          aria-label={`Seleccionar ${row.org_name || 'organización'}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px 0`, verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '36px' }}>
                        <div
                          style={{
                            width: '3px',
                            flexShrink: 0,
                            background: st.border,
                            borderRadius: '0 2px 2px 0',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '6px', padding: `2px ${padH + 2}px 2px ${padH + 2}px`, alignItems: 'center' }}>
                          <div
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '7px',
                              background: isDarkMode ? '#27272a' : '#f0ebe3',
                              display: 'grid',
                              placeItems: 'center',
                              fontWeight: 800,
                              fontSize: '12px',
                              color: text,
                              flexShrink: 0,
                            }}
                          >
                            {initial}
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() => onRowNameClick?.(row)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontWeight: 700,
                                fontSize: '12px',
                                color: '#2563eb',
                                lineHeight: 1.2,
                              }}
                            >
                              {row.org_name || '—'}
                            </button>
                            <div style={{ fontSize: '11px', color: muted, marginTop: '1px', lineHeight: 1.2 }}>
                              {row.rut || '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '200px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>{u1 || '—'}</div>
                      {u2 ? (
                        <div style={{ fontSize: '11px', fontWeight: 500, color: muted, marginTop: '2px', lineHeight: 1.2 }}>{u2}</div>
                      ) : null}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '200px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>{c1 || '—'}</div>
                      {c2 ? (
                        <div style={{ fontSize: '12px', fontWeight: 600, color: text, marginTop: '2px', lineHeight: 1.2 }}>{c2}</div>
                      ) : null}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '180px' }}>
                      <div style={{ fontSize: '11px', color: text, lineHeight: 1.2, fontWeight: 600 }}>{row.country || '—'}</div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '1px', lineHeight: 1.2 }}>{row.region || '—'}</div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '1px', lineHeight: 1.2 }}>{row.commune || '—'}</div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '260px' }}>
                      <Stacked top={row.segment || '—'} bottom={channelIndustry} topColor={text} bottomColor={muted} mutedColor={muted} />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '180px' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: assigneeName ? text : muted,
                          lineHeight: 1.25,
                        }}
                        title={assigneeName || undefined}
                      >
                        {assigneeName || 'Sin asignar'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: text, lineHeight: 1.2 }}>
                        {Number(row.quotations_count || 0).toLocaleString('es-CL')} cotiz.
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 500, color: muted, marginTop: '2px', lineHeight: 1.2 }}>
                        {Number(row.sales_count || 0).toLocaleString('es-CL')} ventas
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '160px' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: row.payment_terms ? text : muted,
                          lineHeight: 1.25,
                        }}
                        title={row.payment_terms || undefined}
                      >
                        {row.payment_terms || '—'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#ea580c', lineHeight: 1.2 }}>{formatMoneyCompact(row.sales_amount)}</div>
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          color: muted,
                          marginTop: '2px',
                          opacity: isDarkMode ? 0.85 : 0.9,
                          lineHeight: 1.2,
                        }}
                      >
                        {formatMoneyCompact(row.quoted_total)}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '200px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            background: st.bg,
                            color: st.color,
                            border: `1px solid ${st.border}`,
                            lineHeight: 1.25,
                          }}
                        >
                          {st.label}
                        </span>
                        <span
                          style={{
                            fontSize: '11px',
                            color: statusSecondaryActive ? text : muted,
                            lineHeight: 1.25,
                            fontWeight: 500,
                            display: 'block',
                            maxWidth: '180px',
                            wordBreak: 'break-word',
                          }}
                        >
                          {statusSecondary}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table3;
